
import {translate} from '@elements/translations';
import {getLanguageConfig, getTicketConfig} from "~vue/utils/config-helper.ts";
import {addParamsToUrl, assertData, fetcher, isUndefined, addDaysISO} from "~vue/utils/utils.ts";
import {DateStruct} from "~vue/utils/structs.ts";
import CalendarDate from "./CalendarDate.vue";
import {ref, computed, watch, reactive, onMounted, defineComponent, defineEmits} from 'vue';
import {storeToRefs} from 'pinia'
import {useCalendarStore} from '~vue/stores/CalendarStore.ts';
import LoadingSpinner from "../loading/LoadingSpinner.vue";
import {useTicketStore} from '~vue/stores/TicketStore.ts';

export default defineComponent({
    name: 'Calendar',
    components: {
        CalendarDate,
        LoadingSpinner
    },
    props: {
        ticketId: {
            type: String,
            required: true,
        },
        selectedDateStart: {
            type: String
        },
        selectedDateEnd: {
            type: String
        },
        url: {
            type: String,
            required: true
        },
    },
    emits: ['changeDateStart', 'changeAvailableTickets', 'changeDateEnd', 'isLoading', 'changeDayRange', 'changeDynamicPricing','changeTicketPrices','changeValidation', 'changeHasServices', 'showError', 'errorMessage'],
    setup(props, {emit}) {
        const dateConfig = ref<object>({});
        const calendarConfig = ref<object>({});
        const minDate = ref<string>("");
        const maxDate = ref<string>("");
        const error = ref<string>(null);
        const isLoading = ref<boolean>(false);
        const calendarStore = useCalendarStore();
        const {shownCalendarMonth} = storeToRefs(calendarStore);
        const {changeShownCalendarMonth} = calendarStore;
        const defaultPriceGroupId = ref<string>("");
        const ticketStore = useTicketStore();
        const {
            changeHasCalendarView
        } = ticketStore;

        const fetchDates = async (setInitialData: Boolean) => {
            emit('isLoading', true);
            isLoading.value = true;
            const params = {
                ticketId: props.ticketId,
                ...(shownCalendarMonth.value.year !== undefined && {year: shownCalendarMonth.value.year}),
                ...(shownCalendarMonth.value.month !== undefined && {month: shownCalendarMonth.value.month}),
            };

            let url = props.url;
            url = addParamsToUrl(url, new URLSearchParams(params));
            await fetcher(url).then((data) => {
                if(data.success) {
                    assertData(data, DateStruct, 'Calendar', 'fetchDates', 'danger');
                    dateConfig.value = data.calendarConfiguration.calendar;
                    calendarConfig.value = data.calendarConfiguration;
                    minDate.value = data.calendarConfiguration.minDate;
                    maxDate.value = data.calendarConfiguration.maxDate;
                    if (setInitialData) {
                        changeShownCalendarMonth(data.calendarConfiguration.calendar.month, data.calendarConfiguration.calendar.year);
                    }
                    if(calendarConfig.value.selectableDays) {
                        emit('changeDayRange', calendarConfig.value.selectableDays);
                    }
                    if(calendarConfig.value.hasDynamicPricing)  {
                        emit('changeDynamicPricing', calendarConfig.value.hasDynamicPricing);
                    }
                    if(calendarConfig.value.hasServices)  {
                        emit('changeHasServices', calendarConfig.value.hasServices);
                    }
                    if(calendarConfig.value.defaultPriceGroup && calendarConfig.value.defaultPriceGroup.id)  {
                        defaultPriceGroupId.value = calendarConfig.value.defaultPriceGroup.id;
                    }
                    emit('isLoading', false);
                    isLoading.value = false;
                } else {
                    emit('showError', true);
                    emit('errorMessage', data.errorMessage || '');
                    emit('isLoading', false);
                    isLoading.value = false;
                }
            }).catch((error) => {
                console.warn(error);
                emit('changeValidation', false);
                emit('isLoading', false);
                emit('showError', true);
                isLoading.value = false;
            });
        };

        const weekdays = computed<string[]>(() => {
            let weekdays = [];
            for (let i = 0; i < 7; i++) {
                let weekday = new Date(2021, 1, i + 1).toLocaleDateString(getLanguageConfig(), {weekday: 'short'});
                weekdays.push(weekday);
            }
            return weekdays;
        });

        const prevDayCount = (firstDate: string): number => {
            if(firstDate === undefined) {
                return 0;
            }
            let weekday = new Date(firstDate).toLocaleDateString(getLanguageConfig(), {weekday: 'short'});
            let weekdayIndex = +weekdays.value.findIndex((element) => element === weekday);
            return weekdayIndex;
        };

        const getMonthName = (month: number): string => {
            //get full name of month index
            if(month === undefined) {
                return "";
            }
            return new Date(2021, +month - 1, 1).toLocaleDateString(getLanguageConfig(), {month: 'long'});
        };

        const getMonthNumberOfDate = (date: string): number => {
            //get full name of month index
            return new Date(date).getMonth();
        };

        const prevShownCalendar = () => {
            const addMonth = 1;

            if(shownCalendarMonth.value.month === 1) {
                changeShownCalendarMonth(12, shownCalendarMonth.value.year - 1);
            } else {
                changeShownCalendarMonth(shownCalendarMonth.value.month - addMonth, shownCalendarMonth.value.year);
            }
            fetchDates(false);
        };

        const nextShownCalendar = () => {
            const addMonth = 1;

            if(shownCalendarMonth.value.month < 12) {
                changeShownCalendarMonth(shownCalendarMonth.value.month + addMonth, shownCalendarMonth.value.year);
            } else if(shownCalendarMonth.value.month === 12) {
                changeShownCalendarMonth(1, shownCalendarMonth.value.year + 1);
            }
            fetchDates(false);
        }

        const changeSelectedDateStart = (date: string) => {
            emit('changeDateStart', date);
            emit('changeValidation', true);
            if(calendarConfig.value.selectableDays) {
                let endDate = addDaysISO(date, calendarConfig.value.selectableDays - 1);
                emit('changeDateEnd', endDate);
            }
        }

        //is clickable but looks disabled (needed so user can deselect date)
        const checkIfFakeDisabled = (date: string) => {
            // let bool = false;
            // if (!isUndefined(props.selectedDateStart) && props.selectedDateStart > date) {
            //     bool = true;
            // }
            // return bool;
        }

        const checkIfDisabled = (day: object) => {
            let bool = false;
            let date = day.date;
            if (!isUndefined(props.selectedDateStart) && isUndefined(props.selectedDateEnd) && date > addDaysISO(props.selectedDateStart, calendarConfig.value.maxSelectableDays - 1)) {
                bool = true;
            } else if (!isUndefined(props.selectedDateStart) && isUndefined(props.selectedDateEnd) && date > props.selectedDateStart && date < addDaysISO(props.selectedDateStart, calendarConfig.value.minSelectableDays - 1)) {
                bool = true;
            } else if (date < minDate.value || date > maxDate.value) {
                bool = true;
            } else if (day.disabled) {
                bool = true;
            }

            return bool;
        }

        const checkIfPrevDisabled = (): boolean => {
            if(minDate.value === "") {
                return false;
            }
            let monthNumber = getMonthNumberOfDate(minDate.value) + 1;
            let yearNumber = +minDate.value.split("-")[0];
            return (shownCalendarMonth.value.month === monthNumber && shownCalendarMonth.value.year === yearNumber);
        }

        const checkIfNextDisabled = () => {
            if(maxDate.value === "") {
                return false;
            }
            let monthNumber = getMonthNumberOfDate(maxDate.value) + 1;
            let yearNumber = +maxDate.value.split("-")[0];
            return (shownCalendarMonth.value.month === monthNumber && shownCalendarMonth.value.year === yearNumber);
        }

        const onSelectDate = (dateObj: Object) => {
            changeSelectedDateStart(dateObj.date);
            if(calendarConfig.value.hasDynamicPricing && dateObj.priceByPriceGroup) {
                emit('changeTicketPrices', dateObj.priceByPriceGroup);
                emit('changeAvailableTickets', dateConfig.value.dates.find((item: any) => item.dateStart === date)?.availableTickets);
            }
        }

        onMounted(() => {
            fetchDates(true);
            changeHasCalendarView(true);
        });

        return {
            dateConfig,
            error,
            translate,
            weekdays,
            onSelectDate,
            prevDayCount,
            getMonthName,
            shownCalendarMonth,
            changeSelectedDateStart,
            prevShownCalendar,
            nextShownCalendar,
            checkIfDisabled,
            checkIfFakeDisabled,
            checkIfPrevDisabled,
            checkIfNextDisabled,
            defaultPriceGroupId,
            isUndefined,
            isLoading
        }
    }
});
